import { useState, useEffect, useMemo, useCallback } from 'react';
import { DISPLAY_OPTION_DEFINITIONS, DISPLAY_CONFIG_STORAGE_PREFIX } from '../configs';

/**
 * useChartDisplayConfig - Manages chart display option state with localStorage persistence.
 *
 * @param {string} viewKey - Unique key for this view (e.g. 'valuation', 'quadrant')
 * @param {string[]} enabledOptionKeys - Which DISPLAY_OPTION_KEYS apply to this view
 *
 * @returns {{
 *   displayConfig: Object,    // { showIcons: bool, showLabels: bool, ... }
 *   setDisplayOption: Function, // (key, value) => void
 *   hasNonDefaults: boolean,  // true when any option differs from its default
 *   resetToDefaults: Function,  // () => void
 * }}
 *
 * @example
 *   const OPTIONS = [DISPLAY_OPTION_KEYS.ICONS, DISPLAY_OPTION_KEYS.LABELS];
 *   const { displayConfig, setDisplayOption, hasNonDefaults, resetToDefaults } =
 *     useChartDisplayConfig('valuation', OPTIONS);
 *   const { showIcons, showLabels } = displayConfig;
 */
export function useChartDisplayConfig(viewKey, enabledOptionKeys) {
  const storageKey = DISPLAY_CONFIG_STORAGE_PREFIX + viewKey;

  const [config, setConfig] = useState(() => {
    let stored = {};
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) stored = JSON.parse(raw);
    } catch {}

    const initial = {};
    for (const key of enabledOptionKeys) {
      const def = DISPLAY_OPTION_DEFINITIONS[key];
      if (def) {
        initial[key] = stored[key] !== undefined ? stored[key] : def.defaultValue;
      }
    }
    return initial;
  });

  // Persist to localStorage whenever config changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(config));
    } catch {}
  }, [config, storageKey]);

  const setDisplayOption = useCallback((key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaults = {};
    for (const key of enabledOptionKeys) {
      const def = DISPLAY_OPTION_DEFINITIONS[key];
      if (def) defaults[key] = def.defaultValue;
    }
    setConfig(defaults);
  }, [enabledOptionKeys]); // eslint-disable-line

  const hasNonDefaults = useMemo(() => {
    return enabledOptionKeys.some(key => {
      const def = DISPLAY_OPTION_DEFINITIONS[key];
      return def && config[key] !== def.defaultValue;
    });
  }, [config, enabledOptionKeys]); // eslint-disable-line

  return {
    displayConfig: config,
    setDisplayOption,
    hasNonDefaults,
    resetToDefaults,
  };
}
